import { useEffect, useRef } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, ConnectionState } from "livekit-client";
import { logConnectionEvent } from "../Meeting/connectionTelemetry";

type Props = {
  roomId: string;
  userId: string | null;
};

/** Telemetria mínima do piloto — grava em connectionDiagnostics com engine=livekit. */
export default function LiveKitPilotTelemetry({ roomId, userId }: Props) {
  const room = useRoomContext();
  const wasReconnectingRef = useRef(false);

  useEffect(() => {
    if (!room) {
      return;
    }

    const base = {
      roomId,
      userId,
      device: "livekit-pilot" as const,
    };

    const log = (type: Parameters<typeof logConnectionEvent>[0], extra?: object) => {
      logConnectionEvent(type, { ...base, ...extra, engine: "livekit" });
    };

    const onConnected = () => {
      if (wasReconnectingRef.current) {
        log("reconnected");
        wasReconnectingRef.current = false;
      }
    };

    const onReconnecting = () => {
      wasReconnectingRef.current = true;
      log("reconnecting");
    };

    const onDisconnected = () => {
      log("roomLeft", { roomLeftState: "disconnected" });
    };

    const onConnectionState = (state: ConnectionState) => {
      if (state === ConnectionState.Reconnecting) {
        onReconnecting();
      }
    };

    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Reconnecting, onReconnecting);
    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.ConnectionStateChanged, onConnectionState);

    return () => {
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Reconnecting, onReconnecting);
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.ConnectionStateChanged, onConnectionState);
    };
  }, [room, roomId, userId]);

  return null;
}
